import { useCallback, useMemo } from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { buildMsal, API_SCOPE } from "./auth/msal.js";
import { AuthGate } from "./auth/AuthGate.js";
import { buildAdminApi } from "./api/client.js";
import { AppointmentsPage } from "./pages/AppointmentsPage.js";
import { AppointmentDetailPage } from "./pages/AppointmentDetailPage.js";
import { CalendarConfigPage } from "./pages/CalendarConfigPage.js";
import { LocationsPage } from "./pages/LocationsPage.js";
import { ProvidersPage } from "./pages/ProvidersPage.js";
import { SettingsHubPage } from "./pages/SettingsHubPage.js";
import { SchedulePage } from "./pages/SchedulePage.js";
import { AuditLogPage } from "./pages/AuditLogPage.js";
import { AccountPage } from "./pages/AccountPage.js";
import { HealthPage } from "./pages/HealthPage.js";
import { CalendarTroubleshootPage } from "./pages/CalendarTroubleshootPage.js";
import { TestEmailPage } from "./pages/TestEmailPage.js";
import { UnacknowledgedTab } from "./pages/UnacknowledgedTab.js";
import { Layout } from "./components/Layout.js";

const msal = buildMsal();
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
});

export function App() {
  return (
    <MsalProvider instance={msal}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/*
              Teams tab: does its own Teams SSO, so it must render OUTSIDE the
              MSAL AuthGate. The "/*" catch-all keeps every existing route gated
              by AuthGate exactly as before (no static prefix, so the nested
              <Routes> in <Authed /> still match against the full path).
            */}
            <Route
              path="/teams/unacknowledged"
              element={<UnacknowledgedTab />}
            />
            <Route
              path="/*"
              element={
                <AuthGate>
                  <Authed />
                </AuthGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
}

function Authed() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const userEmail = account?.username;

  const getToken = useCallback(async () => {
    const acct = accounts[0];
    if (!acct) throw new Error("Not signed in");
    const result = await instance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: acct,
    });
    return result.accessToken;
  }, [instance, accounts]);

  const api = useMemo(
    () => buildAdminApi(import.meta.env.VITE_API_BASE_URL, getToken),
    [getToken],
  );

  return (
    <Layout
      userEmail={userEmail}
      onSignOut={() => {
        void instance.logoutRedirect();
      }}
    >
      <Routes>
        <Route path="/" element={<AppointmentsPage api={api} />} />
        <Route
          path="/appointments/:id"
          element={<AppointmentDetailPage api={api} />}
        />
        <Route path="/settings" element={<SettingsHubPage />} />
        <Route path="/settings/schedule" element={<SchedulePage api={api} />} />
        <Route
          path="/settings/calendar"
          element={<CalendarConfigPage api={api} />}
        />
        <Route
          path="/settings/locations"
          element={<LocationsPage api={api} />}
        />
        <Route
          path="/settings/providers"
          element={<ProvidersPage api={api} />}
        />
        <Route
          path="/settings/audit"
          element={<AuditLogPage api={api} />}
        />
        <Route path="/settings/account" element={<AccountPage />} />
        <Route path="/settings/health" element={<HealthPage api={api} />} />
        <Route
          path="/settings/troubleshoot"
          element={<CalendarTroubleshootPage api={api} />}
        />
        <Route
          path="/settings/test-email"
          element={<TestEmailPage api={api} />}
        />
      </Routes>
    </Layout>
  );
}
